import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import MySubscriptions from '@/components/dashboard/MySubscriptions';
import { SubscriptionStatusCard } from '@/components/dashboard/SubscriptionStatusCard';
import { CreditUsageLedger } from '@/components/dashboard/CreditUsageLedger';
import { CreditWalletCard } from '@/components/dashboard/CreditWalletCard';
import { LeadWalletCard } from '@/components/dashboard/LeadWalletCard';
import { SubscriptionActionBar } from '@/components/dashboard/SubscriptionActionBar';
import { useActiveSubscription } from '@/hooks/useActiveSubscription';
import { Sparkles, Check, ArrowUpRight, Receipt, Megaphone, Flame, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { format } from 'date-fns';

export default function SubscriptionCenter() {
  const { user, hasRole } = useAuth();
  const sub = useActiveSubscription();

  const audience = hasRole('developer') ? 'developer' : hasRole('agency_admin') ? 'agency' : 'individual';

  const { data: upgrades } = useQuery({
    queryKey: ['upgrade-packages', audience],
    queryFn: async () => {
      const { data } = await supabase
        .from('advertising_packages' as any)
        .select('id, name, tier_label, price_etb, listing_quota, project_quota, banner_ad_quota, allows_banner_ads, search_boost_tier, featured_listing_quota, hot_credits, super_hot_credits, refresh_credits, story_ad_credits, is_popular, sort_order')
        .eq('audience', audience)
        .eq('is_active', true)
        .order('sort_order');
      return ((data as any[]) || []);
    },
  });

  const { data: invoices } = useQuery({
    queryKey: ['subscription-recent-invoices', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from('payment_invoices' as any)
        .select('id, invoice_number, amount_etb, status, paid_at, created_at, advertising_packages(name)')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(5);
      return (data as any[]) || [];
    },
  });

  const { data: myInquiries } = useQuery({
    queryKey: ['my-package-inquiries', user?.id, user?.email],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from('package_inquiries' as any)
        .select('id, status, created_at, package_id, advertising_packages(name, tier_label)')
        .or(`user_id.eq.${user!.id},email.eq.${user!.email?.toLowerCase()}`)
        .order('created_at', { ascending: false })
        .limit(5);
      return (data as any[]) || [];
    },
  });

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Sparkles className="h-6 w-6 text-primary" /> Subscription & Packages</h1>
          <p className="text-sm text-muted-foreground">Manage your plan, quotas, banner ads, and billing in one place.</p>
        </div>
        <Button asChild variant="outline" className="gap-1"><Link to="/advertise">Browse all packages <ArrowUpRight className="h-3.5 w-3.5" /></Link></Button>
      </div>

      <SubscriptionActionBar />

      <SubscriptionStatusCard />

      <CreditWalletCard />

      {(hasRole('agency_admin') || hasRole('developer')) && <LeadWalletCard />}

      {myInquiries && myInquiries.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" /> My Package Requests
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {myInquiries.map((i: any) => {
              const pkgName = i.advertising_packages?.tier_label || i.advertising_packages?.name || 'Package';
              const isWon = i.status === 'won';
              const isLost = i.status === 'lost';
              const isPending = ['new', 'contacted', 'qualified'].includes(i.status);
              return (
                <div key={i.id} className="flex items-center justify-between rounded-lg border p-3 text-sm">
                  <div className="flex items-center gap-3">
                    {isWon ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                      : isLost ? <XCircle className="h-4 w-4 text-destructive" />
                      : <Clock className="h-4 w-4 text-amber-500" />}
                    <div>
                      <p className="font-medium">{pkgName}</p>
                      <p className="text-xs text-muted-foreground">Requested {format(new Date(i.created_at), 'd MMM yyyy')}</p>
                    </div>
                  </div>
                  <Badge variant={isWon ? 'default' : isLost ? 'destructive' : 'secondary'} className="capitalize">
                    {isWon ? 'Approved · Active' : isPending ? 'Pending review' : i.status}
                  </Badge>
                </div>
              );
            })}
            <p className="text-xs text-muted-foreground pt-1">
              Credits become available as soon as our team approves your request.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Credits grid */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Your Credits</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {([
              ['Listings', sub.listings],
              ['Projects', sub.projects],
              ['Hot', sub.hot],
              ['Super Hot', sub.superHot],
              ['Refresh', sub.refresh],
              ['Story Ads', sub.story],
              ['Banner', sub.banners],
            ] as const).map(([label, m]) => (
              <div key={label} className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-lg font-bold tabular-nums">{m.used}<span className="text-muted-foreground text-sm">/{m.quota || '—'}</span></p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3"><Flame className="h-5 w-5 text-orange-500" /><div><p className="font-semibold text-sm">Boosted Listings</p><p className="text-xs text-muted-foreground">{sub.boosts.used} of {sub.boosts.quota || '—'} active</p></div></div>
            <Button asChild size="sm" variant="ghost"><Link to="/dashboard/listings">Manage</Link></Button>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3"><Megaphone className="h-5 w-5 text-primary" /><div><p className="font-semibold text-sm">Banner Ads</p><p className="text-xs text-muted-foreground">{sub.banners.used} of {sub.banners.totalQuota || '—'} running</p></div></div>
            <Button asChild size="sm" variant="ghost"><Link to="/dashboard/my-banners">Manage</Link></Button>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3"><Receipt className="h-5 w-5 text-green-600" /><div><p className="font-semibold text-sm">Invoices</p><p className="text-xs text-muted-foreground">{invoices?.length || 0} recent</p></div></div>
            <Button asChild size="sm" variant="ghost"><Link to="/dashboard/my-invoices">View all</Link></Button>
          </CardContent>
        </Card>
      </div>

      <MySubscriptions />

      <CreditUsageLedger />

      {/* Upgrade options */}
      <Card>
        <CardHeader><CardTitle>Available Packages</CardTitle></CardHeader>
        <CardContent>
          {!upgrades ? <Skeleton className="h-40" /> : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {upgrades.map((p) => {
                const isCurrent = sub.primary?.package_id === p.id;
                return (
                  <Card key={p.id} className={`relative ${isCurrent ? 'border-primary border-2' : ''}`}>
                    {p.is_popular && <Badge className="absolute -top-2 right-3 bg-orange-500 hover:bg-orange-500">Popular</Badge>}
                    {isCurrent && <Badge className="absolute -top-2 left-3">Current</Badge>}
                    <CardContent className="p-5 space-y-3">
                      <div>
                        <p className="font-semibold">{p.tier_label || p.name}</p>
                        <p className="text-2xl font-bold mt-1">{Number(p.price_etb).toLocaleString()} <span className="text-xs font-normal text-muted-foreground">ETB</span></p>
                      </div>
                      <ul className="space-y-1 text-xs">
                        {p.listing_quota > 0 && <li className="flex gap-1.5"><Check className="h-3.5 w-3.5 text-green-600" /> {p.listing_quota} Listings</li>}
                        {p.project_quota > 0 && <li className="flex gap-1.5"><Check className="h-3.5 w-3.5 text-green-600" /> {p.project_quota} Projects</li>}
                        {p.featured_listing_quota > 0 && <li className="flex gap-1.5"><Check className="h-3.5 w-3.5 text-green-600" /> {p.featured_listing_quota} Search Boosts ({p.search_boost_tier})</li>}
                        {p.hot_credits > 0 && <li className="flex gap-1.5"><Check className="h-3.5 w-3.5 text-green-600" /> {p.hot_credits} Hot Credits</li>}
                        {p.super_hot_credits > 0 && <li className="flex gap-1.5"><Check className="h-3.5 w-3.5 text-green-600" /> {p.super_hot_credits} Super Hot Credits</li>}
                        {p.refresh_credits > 0 && <li className="flex gap-1.5"><Check className="h-3.5 w-3.5 text-green-600" /> {p.refresh_credits} Refresh Credits</li>}
                        {p.story_ad_credits > 0 && <li className="flex gap-1.5"><Check className="h-3.5 w-3.5 text-green-600" /> {p.story_ad_credits} Story Ads</li>}
                        {p.allows_banner_ads && p.banner_ad_quota > 0 && <li className="flex gap-1.5"><Check className="h-3.5 w-3.5 text-green-600" /> {p.banner_ad_quota} Banner Ads</li>}
                      </ul>
                      <Button asChild size="sm" className="w-full" disabled={isCurrent} variant={isCurrent ? 'outline' : 'default'}>
                        <Link to="/advertise">{isCurrent ? 'Current plan' : 'Choose'}</Link>
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent invoices */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Recent Invoices</CardTitle>
          <Button asChild size="sm" variant="ghost"><Link to="/dashboard/my-invoices">View all</Link></Button>
        </CardHeader>
        <CardContent>
          {!invoices?.length ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No invoices yet.</p>
          ) : (
            <div className="divide-y">
              {invoices.map((i) => (
                <div key={i.id} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium">{i.invoice_number}</p>
                    <p className="text-xs text-muted-foreground">{i.advertising_packages?.name || '—'} · {format(new Date(i.created_at), 'd MMM yyyy')}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="tabular-nums">{Number(i.amount_etb).toLocaleString()} ETB</span>
                    <Badge variant={i.status === 'paid' ? 'default' : 'secondary'} className="capitalize">{i.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
